import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import Actions from "../login/duck/actions";

import Operations from "../login/duck/operations";
import { API_URL } from "./constants";
import {
  hasLoginToken,
  markHandoffPending,
  sanitizeRedirectPath,
} from "./helpers/deepLinkHandoff";

/**
 * Logs the user in from a `?token=…&redirectTo=…` deep link (email, WhatsApp,
 * Forge login-as) and reloads into `redirectTo`.
 *
 * Returns `isSigningIn`: true from the very first render of a token link until
 * the flow either hands off to the reload (stays true — the page is going
 * away) or stops short (failure, a declined account switch, or a new user who
 * must finish registering). `DeepLinkLoader` covers the page meanwhile.
 */
export function useLoginTokenToRenew() {
  const dispatch = useDispatch();
  const userIsAuthenticated = useSelector(
    (state) => state.user.isAuthenticated
  );
  const user = useSelector((state) => state.user.profile);
  // Read synchronously so the loader is up before the homepage paints.
  const [isSigningIn, setIsSigningIn] = useState(() =>
    hasLoginToken(window.location.search)
  );

  const loginUser = useCallback(
    (data, redirectPath) => {
      dispatch(
        Operations.loginUserSuccess(
          data.accessToken,
          data.refreshToken,
          data.profile,
          data?.isAccountReactivated || false
        )
      );

      // Keep the loader up through the reload and until the target settles.
      markHandoffPending(redirectPath);
      window.location.href = redirectPath;
    },
    [dispatch]
  );

  const failLogin = useCallback((error) => {
    console.error(error);
    setIsSigningIn(false);
    toast.error("Something went wrong please try again.");
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");

    if (token) {
      // A truncated or mangled link (mail clients wrap long URLs) must fail
      // visibly rather than leave the loader up forever.
      let tokenClaims;
      try {
        tokenClaims = jwt_decode(token);
      } catch (error) {
        failLogin(error);
        return;
      }

      if (userIsAuthenticated && tokenClaims.id !== user._id) {
        const shouldProceed = window.confirm(
          `You are about to be logged in as ${tokenClaims.email}. Continue?`
        );

        if (!shouldProceed) {
          setIsSigningIn(false);
          return;
        }

        dispatch(Actions.logout());
      }
      const redirectPath = sanitizeRedirectPath(queryParams.get("redirectTo"));
      const userObject = tokenClaims;

      if (userObject.isNew) {
        dispatch(
          Actions.setContactInfo({
            phone: userObject.phone,
            firstName: userObject.firstName,
            lastName: userObject.lastName,
            // Proof minted server-side when the WhatsApp login link was built.
            phoneVerificationToken: userObject.phoneVerificationToken,
          })
        );
        dispatch(Actions.authProvider(userObject.authProvider));
        dispatch(Actions.showOAuthRegisterModal(true));
        dispatch(Actions.redirectAfterLogin(redirectPath));
        // The register modal takes over; the user has something to do now.
        setIsSigningIn(false);
        return;
      }
      // A login-as link (Forge, via /auth/idp) carries an access token, which
      // /auth/renew refuses. It is the session as is: with an empty refresh
      // token beside it, the session ends when the token expires, and
      // VerifyEmail knows not to send the operator to /verify-email.
      if (userObject.tokenType === "access") {
        axios
          .get(`${API_URL}/v1/game-player/search`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(({ data: profile }) => {
            delete profile.refreshTokens;
            loginUser(
              { accessToken: token, refreshToken: "", profile },
              redirectPath
            );
          })
          .catch(failLogin);
        return;
      }
      axios
        .post(`${API_URL}/v1/auth/renew/`, {
          refreshToken: token,
          tokenLogin: true,
        })
        .then(({ data }) => {
          loginUser(data, redirectPath);
        })
        .catch(failLogin);
    }
  }, [dispatch, loginUser, failLogin]);

  return { isSigningIn };
}
